import { useCallback, useEffect, useMemo } from 'react';
import { Search } from '@mui/icons-material';
import { InputAdornment, TextField } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import type { GridRowId } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  useAdminUsersQuery,
  useUpdateAdminUserMutation,
  useUserQuery,
  type Role,
  type UserPlan,
} from '../../../api/gql';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { PageHeader } from '../../../components/PageHeader';
import { useControlledRowStateModel, useControlledTableModel } from '../../../hooks/useControlledTableModel';
import { PageContent, PageLayout } from '../../../layout';
import { notify } from '../../../utils/notification';
import { AdminUsersTable } from './modules/AdminUsersTable';

export const AdminUsersPage = () => {
  const { t } = useTranslation('page-admin-users');
  const client = useQueryClient();

  const { query, debouncedQuery, skip, handlers, paginationModel } = useControlledTableModel({
    pageSize: 25,
  });

  const { data: userData, isLoading: isLoadingCurrentUser, error: userError } = useUserQuery();
  const currentUserId = userData?.user.id;

  const {
    data,
    isFetching: isFetchingUsers,
    isError,
    error: adminUsersError,
  } = useAdminUsersQuery(
    { query: debouncedQuery, take: paginationModel.pageSize, skip: skip },
    {
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    if (!(userError ?? adminUsersError)) return;

    console.error(userError ?? adminUsersError);
    notify.negative({ message: t('query.error') });
  }, [userError, adminUsersError, t]);

  const { mutate: updateAdminUser, isPending: isUpdating } = useUpdateAdminUserMutation({
    onSuccess: async () => {
      notify.positive({
        message: t('update.success'),
      });
      await client.invalidateQueries({ queryKey: ['adminUsers'], refetchType: 'active' });
    },
    onError: (error) => {
      console.error(error?.message ?? error);
      notify.negative({
        message: t('update.error'),
      });
    },
  });

  const onRoleChange = useCallback(
    (userId: GridRowId, role: Role) => {
      updateAdminUser({ userId: String(userId), input: { role } });
    },
    [updateAdminUser],
  );

  const onPlanChange = useCallback(
    (userId: GridRowId, plan: UserPlan) => {
      updateAdminUser({ userId: String(userId), input: { plan } });
    },
    [updateAdminUser],
  );

  const { rowCount } = useControlledRowStateModel(data?.adminUsers.totalCount);
  const users = useMemo(() => data?.adminUsers.results, [data]);

  return (
    <PageLayout>
      <Grid2 container>
        <Grid2 xs={12} md={4}>
          <PageHeader title={t('title')} backRouteTo={null} />
        </Grid2>
        <Grid2 xs={12} md={8}>
          <TextField
            fullWidth
            placeholder={t('search') ?? ''}
            onChange={(e) => handlers.setQuery(e.target.value)}
            value={query}
            InputProps={{
              sx: { bgcolor: 'background.paper' },
              startAdornment: (
                <InputAdornment position={'start'}>
                  <Search color={'primary'} />
                </InputAdornment>
              ),
            }}
          />
        </Grid2>
      </Grid2>
      <PageContent
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          position: 'relative',
        }}
      >
        {isError ? <ExpandedBox>{t('query.error')}</ExpandedBox> : null}
        <AdminUsersTable
          currentUserId={currentUserId}
          users={users ?? []}
          loading={isLoadingCurrentUser || isFetchingUsers}
          paginationModel={paginationModel}
          onPaginationModelChange={handlers.handlePageChange}
          paginationMode={'server'}
          rowCount={rowCount}
          onRoleChange={onRoleChange}
          onPlanChange={onPlanChange}
          isUpdating={isUpdating}
        />
      </PageContent>
    </PageLayout>
  );
};
